import {
	buildAtlAttributionHeaders,
	type MercuryFocusAreaType,
	mercuryFocusAreaTypesQuery,
} from '@atlassian/search-client';

import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import {
	type FetchedSelectFilterOption,
	type SelectFilterState,
} from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys, type ProductKeys1P } from '../../../../../common/constants/products';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

/**
 * Queries all focus area types from graphQL and returns all options as suggested but unselected
 * @param config
 * @returns
 */
const getInitialOptions = async (config: CloudConfig): Promise<FetchedSelectFilterOption[]> => {
	const headers = buildAtlAttributionHeaders({
		cloudId: config.cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});
	const focusAreaTypesResponse = await mercuryFocusAreaTypesQuery(config.cloudId || '', headers);
	const focusAreaTypes: MercuryFocusAreaType[] =
		focusAreaTypesResponse.data?.mercury.focusAreaTypes ?? [];

	return focusAreaTypes.map((focusAreaType) => ({
		trackingKey: focusAreaType.id,
		label: focusAreaType.name,
		value: focusAreaType.id,
		queryParamValue: focusAreaType.id,
		product: config.product,
	}));
};

/**
 * Queries all focus area types from graphQL and returns all options as suggested and query params are suggested
 * @param queryParams
 * @param config
 * @returns
 */
const getOptionsWithSelectionsFromQueryParams = async (
	queryParams: string[],
	config: CloudConfig,
): Promise<FetchedSelectFilterOption[]> => {
	const focusAreaTypeOptions = await getInitialOptions(config);
	return focusAreaTypeOptions.filter((option) => queryParams.includes(option.value));
};

/**
 * Queries all focus area types from graphQL and filters out options that do not match the text query
 * @param query
 * @param config
 * @returns
 */
const getOptionsFilteredByTextQuery = async (
	query: string,
	config: CloudConfig,
): Promise<FetchedSelectFilterOption[]> => {
	const focusAreaTypeOptions = await getInitialOptions(config);
	return focusAreaTypeOptions.filter((option) =>
		option.label.toLowerCase().includes(query.toLowerCase()),
	);
};

const productKeys1P: ProductKeys1P[] = [ProductKeys.Focus];

export const filter: Readonly<{
	id: 'focusAreaType';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'focusAreaType'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'focusAreaType'>;
	getInitialState: (
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'focusAreaType'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'focusAreaType',
	products: productKeys1P,
	alwaysVisible: true,
	universal: false,
	queryParams: {
		key: 'focusAreaTypes',
		fetcher: getOptionsWithSelectionsFromQueryParams,
	},
	initialOptions: {
		fetcher: getInitialOptions,
	},
	lookup: {
		fetcher: getOptionsFilteredByTextQuery,
	},
});
